import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { useLocation, useSearchParams } from 'react-router-dom'

import { isNavActive } from '@/lib/navigation'
import { settingsPages, settingsTabs } from '@/config/sidebar'

import { SectionLink, SectionNav } from './SectionNav'

/** The settings group's pages, with `/settings` expanded into its tabs. Feature flags apply as in the main nav. */
function useSettingsLinks(): SectionLink[] {
  const { getSidebarItems } = useFeatureFlags()
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const tab = params.get('tab') ?? settingsTabs[0].value
  const pages = getSidebarItems().find((item) => item.id === 'settings')?.children ?? []
  // The longest matching route wins, so /orders/methods lights up Delivery methods only.
  const current = pages
    .flatMap((p) => (p.to && isNavActive(pathname, p.to) ? [p.to] : []))
    .sort((a, b) => b.length - a.length)[0]

  return settingsPages(pages).map((page) => {
    const [path, query] = page.to.split('?tab=')
    const active = path === '/settings' ? current === '/settings' && tab === (query ?? settingsTabs[0].value) : current === page.to
    return { ...page, active }
  })
}

interface SettingsNavProps {
  /** Where "‹ Settings" goes: the last page visited outside settings. */
  exitTo: string
  /** Called after a link is followed (closes the mobile sheet). */
  onNavigate?: () => void
}

/** The settings column beside the icon rail on settings routes. */
export const SettingsNav = ({ exitTo, onNavigate }: SettingsNavProps) => (
  <SectionNav title="Settings" exitTo={exitTo} links={useSettingsLinks()} onNavigate={onNavigate} />
)
